import classNames from 'classnames'
import { ErrorMessage } from 'formik'
import { isValidImage, isValidSize } from 'lib/helpers'
import * as React from 'react'
import { ChangeEvent, ReactElement, useEffect, useState } from 'react'

export type ImageContainerProps = {
  text: string
  handleFileChange: Function
  setFieldValue: Function
  errors: any
  name: string
  disabled?: boolean
  shouldShowInfo?: boolean
  customLabel?: ReactElement
}

export const ImageContainer = (props: ImageContainerProps) => {
  const {
    text,
    handleFileChange,
    setFieldValue,
    errors,
    shouldShowInfo = true,
    disabled = false,
    customLabel,
    name,
  } = props

  const [errorMessage, setErrorMessage] = useState<string>('')

  const beforeHandleFileChange = async (
    eventChange: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: Function,
    name: string,
    errors: any,
  ) => {
    const event = { ...eventChange }
    const { files } = event.target

    if (!files || !files.length) return
    let file: File | null = files[0]

    // Clean up error message
    setErrorMessage('')

    // Validate File type and Size
    const imageValid = await isValidImage(file)
    const sizeValid = isValidSize(file)

    // eslint-disable-next-line no-prototype-builtins
    const formShowingFieldError = errors.hasOwnProperty(name)
    const somethingIsInvalid = !imageValid || !sizeValid

    // #region Handle validation messages

    // Clear the file uploader when file is invalid
    if (somethingIsInvalid) {
      event.target.value = ''
      file = null
    }

    // Show correct message when 2 conditions are met:
    // 1 - File is invalid
    // 2 - Parent is not showing an error in the same field
    if (somethingIsInvalid && !formShowingFieldError) {
      const ERROR_FILE_INVALID =
        'Invalid file format. Please use .png or .webp for static images, and .gif for animated images.'
      const ERROR_FILE_TOO_BIG = 'The file you uploaded is too heavy, please compress it or upload a smaller file'

      const allInvalid = !imageValid && !sizeValid
      const error = allInvalid ? ERROR_FILE_INVALID : !imageValid ? ERROR_FILE_INVALID : ERROR_FILE_TOO_BIG
      setErrorMessage(error)
    }

    // #endregion

    handleFileChange(file, setFieldValue, name)
  }

  // On form errors update - Clean up state
  useEffect(() => {
    setErrorMessage('')
  }, [errors])

  return (
    <div className={classNames('date-picker-container', !shouldShowInfo && 'h78')}>
      {customLabel ? <span>{React.cloneElement(customLabel)}</span> : <label>{text}</label>}
      <input
        type="file"
        accept="image/png, image/gif, image/webp"
        disabled={disabled}
        className={classNames(Boolean(errors?.[name]) && 'error')}
        onChange={(e: ChangeEvent<HTMLInputElement>) => beforeHandleFileChange(e, setFieldValue, name, errors)}
      />

      <ErrorMessage name={name} component="p" className="bk-error" />
      {errorMessage && <p className="bk-error">{errorMessage}</p>}
      {shouldShowInfo && (
        <div className="input-field-helper">
          Image requirements:
          <ul>
            <li>Mandatory: PNG, GIF or WebP format</li>
            <li>Recommended: measures 500x500px, round shape, size less than 200KB (Max. 4MB)</li>
            <li>Animated pngs are not supported. Use of gif format recommended.</li>
          </ul>
        </div>
      )}
    </div>
  )
}
